import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router';
import { Button, Input } from '@mieweb/ui';
import { startAuthentication, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill } from '@simplewebauthn/browser';
import * as api from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { useAuthStore } from '../lib/store';
import { isDesktop } from '../lib/desktop';
import { trackEvent } from '../lib/analytics';
import { BrandLockup } from '../components/BrandMark';
import { SocialLoginButtons } from '../components/SocialLoginButtons';

type Step = 'email' | 'code' | 'mfa';

export function LoginPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [supportsPasskeys, setSupportsPasskeys] = useState(false);
  const [supportsAutofill, setSupportsAutofill] = useState(false);
  const [socialAvailable, setSocialAvailable] = useState(false);

  // MFA state
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');

  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get redirect URL from query param first, then fallback to state, then to dashboard
  const redirectParam = searchParams.get('redirect');
  const defaultRedirect = (location.state as { from?: Location })?.from?.pathname || '/dashboard';

  // Detect signup mode from query param (e.g. /login?mode=signup)
  const isSignup = searchParams.get('mode') === 'signup';

  // Pre-fill email from query param (e.g. /login?email=will@reiske.me).
  const emailParam = searchParams.get('email');
  const autosendParam = searchParams.get('autosend') === 'true';
  useEffect(() => {
    if (!emailParam) return;
    setEmail((current) => (current ? current : emailParam));
  }, [emailParam]);

  const [hasAutosent, setHasAutosent] = useState(false);

  // Surface OAuth errors handed back via ?oauth_error=… from the callback.
  const oauthError = searchParams.get('oauth_error');
  useEffect(() => {
    if (oauthError) setError(oauthError);
  }, [oauthError]);

  // Resume the MFA step when social login lands on a 2FA-protected account.
  const oauthMfaHandledRef = useRef(false);
  useEffect(() => {
    if (oauthMfaHandledRef.current) return;
    const navState = location.state as
      | { oauthMfa?: { mfaToken: string; email?: string } }
      | null;
    if (navState?.oauthMfa?.mfaToken) {
      oauthMfaHandledRef.current = true;
      setMfaToken(navState.oauthMfa.mfaToken);
      if (navState.oauthMfa.email) setEmail(navState.oauthMfa.email);
      setStep('mfa');
    }
  }, [location.state]);

  // Check passkey support on mount. Passkeys are disabled in the desktop
  // shell: WebAuthn RP-ID validation rejects the tauri:// origin.
  useEffect(() => {
    const checkSupport = async () => {
      if (isDesktop) {
        setSupportsPasskeys(false);
        setSupportsAutofill(false);
        return;
      }
      setSupportsPasskeys(browserSupportsWebAuthn());
      try {
        const autofillSupport = await browserSupportsWebAuthnAutofill();
        setSupportsAutofill(autofillSupport);
      } catch {
        setSupportsAutofill(false);
      }
    };
    checkSupport();
  }, []);

  // Handle post-login navigation
  const handlePostLogin = useCallback((userData?: { organizations?: Array<{ id: string }> }) => {
    const storeState = useAuthStore.getState();
    const hasOrganization = !!storeState.currentOrganization ||
      storeState.organizations.length > 0 ||
      (userData?.organizations && userData.organizations.length > 0);

    // For signups, always go to onboarding or dashboard — never restore a previous path
    if (isSignup) {
      navigate(hasOrganization ? '/dashboard' : '/onboarding', { replace: true });
      return;
    }

    // Don't redirect to admin pages unless the user is a site admin
    const user = storeState.user;
    let destination = redirectParam || (hasOrganization ? defaultRedirect : '/onboarding');
    if (destination.startsWith('/admin') && !user?.isSiteAdmin) {
      destination = '/dashboard';
    }

    navigate(destination, { replace: true });
  }, [redirectParam, defaultRedirect, navigate, isSignup]);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated) {
      handlePostLogin();
    }
  }, [isAuthenticated, isAuthLoading, handlePostLogin]);

  // Try conditional UI (autofill) on mount — only on the email step, since
  // browser autofill requires the email input with autocomplete="webauthn"
  // to be present in the DOM (it isn't on the code/MFA steps).
  useEffect(() => {
    if (!supportsAutofill || isAuthenticated || step !== 'email') return;

    let cancelled = false;

    const tryConditionalUI = async () => {
      try {
        const options = await api.getPasskeyAuthenticationOptions();
        if (cancelled) return;

        const authResp = await startAuthentication({
          optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
          useBrowserAutofill: true,
        });
        if (cancelled) return;

        // Verify with server
        const result = await api.verifyPasskeyAuthentication({
          ...authResp,
          _challengeId: (options as Record<string, unknown>)._challengeId,
        });

        // Past this point the user has actively authenticated — do NOT
        // bail out on `cancelled` because the cleanup may have fired due
        // to a store-triggered re-render (e.g. setTokens causing a new
        // handlePostLogin reference).  We must finish the login flow.

        if (result.verified) {
          // Check if MFA is required
          if ((result as unknown as { mfaRequired?: boolean }).mfaRequired) {
            const mfaResult = result as unknown as { mfaRequired: boolean; mfaToken: string };
            setMfaToken(mfaResult.mfaToken);
            setStep('mfa');
            return;
          }

          const { setTokens, setUser, setOrganizations, setCurrentOrganization } = useAuthStore.getState();
          setTokens(result.accessToken, result.refreshToken);
          const userData = await api.getCurrentUser();
          setOrganizations(userData.organizations || []);
          if (userData.organizations?.length > 0) {
            setCurrentOrganization(userData.organizations[0] ?? null);
          }
          setUser(userData);
          // Navigate directly — don't rely solely on the isAuthenticated useEffect
          handlePostLogin(userData);
        } else {
          setError('Passkey authentication failed. Please try again.');
        }
      } catch (err) {
        // User dismissing the prompt or browser aborting is expected — ignore.
        if (err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
          return;
        }
        // Show real errors to the user so they aren't stuck on a blank screen.
        const message = err instanceof Error ? err.message : 'Passkey authentication failed';
        setError(message);
        console.error('Conditional UI passkey error:', err);
      }
    };

    tryConditionalUI();

    return () => {
      cancelled = true;
    };
  }, [supportsAutofill, isAuthenticated, step, handlePostLogin]);

  // Handle passkey login (modal flow)
  const handlePasskeyLogin = async () => {
    setError('');
    setIsPasskeyLoading(true);

    try {
      // Get authentication options (no email = discoverable credentials)
      const options = await api.getPasskeyAuthenticationOptions();

      // Start authentication with the authenticator
      const authResp = await startAuthentication({
        optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });

      // Send to server for verification
      const result = await api.verifyPasskeyAuthentication({
        ...authResp,
        _challengeId: (options as Record<string, unknown>)._challengeId,
      });

      if (result.verified) {
        // Check if MFA is required
        if ((result as unknown as { mfaRequired?: boolean }).mfaRequired) {
          const mfaResult = result as unknown as { mfaRequired: boolean; mfaToken: string };
          setMfaToken(mfaResult.mfaToken);
          setStep('mfa');
          setIsPasskeyLoading(false);
          return;
        }

        // Set tokens and user data
        const { setTokens, setUser, setOrganizations, setCurrentOrganization } = useAuthStore.getState();
        setTokens(result.accessToken, result.refreshToken);
        const userData = await api.getCurrentUser();
        setOrganizations(userData.organizations || []);
        if (userData.organizations?.length > 0) {
          setCurrentOrganization(userData.organizations[0] ?? null);
        } else {
          setCurrentOrganization(null);
        }
        setUser(userData);
        // Navigate directly — don't rely solely on the isAuthenticated useEffect
        handlePostLogin(userData);
      } else {
        setError('Passkey authentication failed');
      }
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Authentication was cancelled');
        } else {
          setError(err.message);
        }
      } else {
        setError('Passkey authentication failed');
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const sendCode = useCallback(async (emailToSend: string) => {
    setError('');
    setIsLoading(true);

    try {
      await api.sendOtp(emailToSend);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // If `autosend=true` is present alongside `email`, automatically submit the
  // email step so the user lands directly on the code-entry screen.
  useEffect(() => {
    if (!autosendParam || hasAutosent) return;
    if (!emailParam) return;
    if (isAuthLoading || isAuthenticated) return;
    if (step !== 'email') return;
    // Basic email shape check so we don't auto-fire requests for garbage input
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailParam)) return;
    setHasAutosent(true);
    void sendCode(emailParam);
  }, [autosendParam, emailParam, hasAutosent, isAuthLoading, isAuthenticated, step, sendCode]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendCode(email);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Call verifyOtp directly to check for MFA requirement
      const result = await api.verifyOtp(email, code);
      
      // Check if MFA is required
      if ((result as unknown as { mfaRequired?: boolean }).mfaRequired) {
        const mfaResult = result as unknown as { mfaRequired: boolean; mfaToken: string };
        setMfaToken(mfaResult.mfaToken);
        setStep('mfa');
        setIsLoading(false);
        return;
      }

      // No MFA required - use the tokens from the verify result directly
      // (calling login() would call verifyOtp a second time, but the OTP is already consumed)
      if (result.isNewUser) {
        trackEvent('sign_up', { method: 'email_otp' });
      }
      const { setTokens, setUser, setOrganizations, setCurrentOrganization } = useAuthStore.getState();
      setTokens(result.accessToken, result.refreshToken);
      const userData = await api.getCurrentUser();
      setOrganizations(userData.organizations || []);
      if (userData.organizations?.length > 0) {
        setCurrentOrganization(userData.organizations[0] ?? null);
      } else {
        setCurrentOrganization(null);
      }
      setUser(userData);
      // Navigate directly — don't rely solely on the isAuthenticated useEffect
      handlePostLogin(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code');
      setIsLoading(false);
    }
  };

  // Handle MFA verification
  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let result: api.MfaVerifyResult;

      if (useRecoveryCode) {
        result = await api.verifyMfaRecoveryCode(mfaToken, recoveryCode);
      } else {
        result = await api.verifyMfaCode(mfaToken, mfaCode);
      }

      if (result.verified) {
        // Set tokens and user data
        const { setTokens, setUser, setOrganizations, setCurrentOrganization } = useAuthStore.getState();
        setTokens(result.accessToken, result.refreshToken);
        const userData = await api.getCurrentUser();
        setOrganizations(userData.organizations || []);
        if (userData.organizations?.length > 0) {
          setCurrentOrganization(userData.organizations[0] ?? null);
        } else {
          setCurrentOrganization(null);
        }
        setUser(userData);

        // Show warning if recovery codes are low
        if (result.recoveryCodesRemaining !== undefined && result.recoveryCodesRemaining <= 2) {
          // User will see this after redirect to dashboard
          console.warn(`Only ${result.recoveryCodesRemaining} recovery codes remaining`);
        }

        // Navigate directly — don't rely solely on the isAuthenticated useEffect
        handlePostLogin(userData);
      } else {
        setError('Verification failed');
        setIsLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code');
      setIsLoading(false);
    }
  };

  // Show loading spinner while checking auth status or after successful login
  if (isAuthLoading || isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
              <BrandLockup
                iconClassName="h-8 w-8"
                textClassName="text-2xl font-bold"
                mode="dark"
              />
            </Link>
          </div>
          
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold mb-4">
                Let feedback spread.
                <br />
                <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 bg-clip-text text-transparent">
                  Let ideas grow.
                </span>
              </h2>
              <p className="text-slate-300 text-lg max-w-md">
                Collect and act on user feedback to understand your users and build better products.
              </p>
            </div>
            
            {/* Feature Highlights */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <span className="text-amber-400">⚡</span>
                </div>
                <div>
                  <p className="font-medium">Setup in minutes</p>
                  <p className="text-sm text-slate-400 dark:text-neutral-500">Add feedback collection with just 2 lines of code</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <span className="text-orange-400">🔒</span>
                </div>
                <div>
                  <p className="font-medium">Secure by default</p>
                  <p className="text-sm text-slate-400 dark:text-neutral-500">HIPAA-ready with enterprise-grade security</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
                  <span className="text-rose-400">📊</span>
                </div>
                <div>
                  <p className="font-medium">Real-time insights</p>
                  <p className="text-sm text-slate-400 dark:text-neutral-500">Watch feedback stream in live as users respond</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-8 text-sm text-slate-400 dark:text-neutral-500">
            <span>Trusted by innovative teams</span>
            <div className="flex items-center gap-4 opacity-60">
              <img 
                src="/logos/bluehive.png" 
                alt="BlueHive" 
                className="h-5 w-auto brightness-0 invert"
              />
              <img 
                src="/logos/ozwell.png" 
                alt="Ozwell" 
                className="h-5 w-auto brightness-0 invert"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <BrandLockup
                iconClassName="h-8 w-8"
                textClassName="text-2xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              {step === 'email'
                ? (isSignup ? 'Get started free' : 'Welcome back')
                : step === 'mfa' ? 'Two-factor authentication' : 'Check your email'}
            </h1>
            <p className="mt-2 text-slate-600 dark:text-neutral-400">
              {step === 'email' 
                ? (isSignup ? 'Create your account — no credit card required' : 'Sign in to your account to continue')
                : step === 'mfa'
                ? 'Enter the code from your authenticator app'
                : `We sent a 6-digit code to ${email}`
              }
            </p>
          </div>

          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-slate-200 dark:border-neutral-800 p-8 shadow-xl shadow-slate-200/50 dark:shadow-neutral-950/50">
            {step === 'email' ? (
              <form onSubmit={handleSendCode} className="space-y-6">
                <div>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoFocus
                    label="Email address"
                    size="lg"
                    autoComplete="username webauthn"
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-700/40 p-4 text-sm text-red-700 dark:text-red-400 flex items-start gap-3">
                    <span className="text-red-500 mt-0.5">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  isLoading={isLoading}
                  loadingText="Sending code..."
                  fullWidth
                  size="lg"
                >
                  {isSignup ? 'Create Account →' : 'Continue with Email →'}
                </Button>

                {(supportsPasskeys || socialAvailable) && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200 dark:border-neutral-800" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white dark:bg-neutral-900 text-slate-500 dark:text-neutral-400">Or continue with</span>
                    </div>
                  </div>
                )}

                {supportsPasskeys && (
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    size="lg"
                    onClick={handlePasskeyLogin}
                    disabled={isPasskeyLoading}
                    isLoading={isPasskeyLoading}
                    loadingText="Authenticating..."
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>🔐</span>
                      Sign in with Passkey
                    </span>
                  </Button>
                )}

                <SocialLoginButtons
                  redirect={redirectParam}
                  mode={isSignup ? 'signup' : undefined}
                  onAvailabilityChange={setSocialAvailable}
                />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200 dark:border-neutral-800" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white dark:bg-neutral-900 text-slate-500 dark:text-neutral-400">No password needed</span>
                  </div>
                </div>

                <p className="text-center text-sm text-slate-500 dark:text-neutral-400">
                  {isSignup ? (
                    <>We'll email you a magic code — no password needed. <br />Already have an account? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link></>
                  ) : (
                    <>We'll send you a magic code to sign in securely. <br />New to Pollenate? <Link to="/login?mode=signup" className="text-primary font-medium hover:underline">Create an account</Link></>
                  )}
                </p>
              </form>
            ) : step === 'code' ? (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <span className="text-3xl">✉️</span>
                  </div>
                </div>

                <div>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                    label="Verification code"
                    size="lg"
                    className="text-center text-2xl font-mono tracking-[0.5em]"
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-700/40 p-4 text-sm text-red-700 dark:text-red-400 flex items-start gap-3">
                    <span className="text-red-500 mt-0.5">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  isLoading={isLoading}
                  loadingText="Verifying..."
                  fullWidth
                  size="lg"
                >
                  Verify & Sign In
                </Button>

                <div className="flex items-center justify-center gap-2 text-sm">
                  <span className="text-slate-500 dark:text-neutral-400">Didn't receive the code?</span>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setCode('');
                      setError('');
                    }}
                    className="text-primary font-medium hover:underline"
                  >
                    Try again
                  </button>
                </div>
              </form>
            ) : step === 'mfa' ? (
              <form onSubmit={handleMfaVerify} className="space-y-6">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <span className="text-3xl">🛡️</span>
                  </div>
                </div>

                {!useRecoveryCode ? (
                  <div>
                    <Input
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      required
                      autoFocus
                      label="Authenticator code"
                      size="lg"
                      autoComplete="one-time-code"
                      className="text-center text-2xl font-mono tracking-[0.5em]"
                    />
                  </div>
                ) : (
                  <div>
                    <Input
                      id="recovery-code"
                      type="text"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value.toLowerCase())}
                      placeholder="xxxx-xxxx"
                      maxLength={9}
                      required
                      autoFocus
                      label="Recovery code"
                      size="lg"
                      className="text-center text-xl font-mono tracking-wider"
                    />
                  </div>
                )}

                {error && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-700/40 p-4 text-sm text-red-700 dark:text-red-400 flex items-start gap-3">
                    <span className="text-red-500 mt-0.5">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading || (!useRecoveryCode ? mfaCode.length !== 6 : recoveryCode.length < 8)}
                  isLoading={isLoading}
                  loadingText="Verifying..."
                  fullWidth
                  size="lg"
                >
                  Verify
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setUseRecoveryCode(!useRecoveryCode);
                      setMfaCode('');
                      setRecoveryCode('');
                      setError('');
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    {useRecoveryCode ? 'Use authenticator code instead' : 'Use a recovery code instead'}
                  </button>
                </div>

                <div className="flex items-center justify-center gap-2 text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setMfaCode('');
                      setRecoveryCode('');
                      setMfaToken('');
                      setUseRecoveryCode(false);
                      setError('');
                    }}
                    className="text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300"
                  >
                    ← Back to login
                  </button>
                </div>
              </form>
            ) : null}
          </div>

          <p className="mt-8 text-center text-sm text-slate-500 dark:text-neutral-400">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          </p>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300 transition-colors">
              ← Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
